/**
 * Replay files and where the latest one is kept. A replay names its mission by how to rebuild
 * it (generator parameters, or the hand-built depot's seed), never by the map itself.
 */

import { generateMission } from '../gen';
import { browserStorage } from '../platform/storage';
import { buildMunitionsDepot } from '../sim/missions/munitionsDepot';
import type { Loadout, MissionDef, MissionParams, Outcome, SimMods } from '../sim/types';
import type { ReplayRun } from './replayCodec';

export type MissionSource = { kind: 'generated'; params: MissionParams } | { kind: 'depot'; seed: number };

/** What the mission hands back when it ends: the commands, and the hash they must reproduce. */
export interface Recording {
  runs: ReplayRun[];
  ticks: number;
  finalHash: string;
  outcome: Outcome | null;
}

export interface ReplayFile extends Recording {
  version: 1;
  source: MissionSource;
  loadout: Loadout | null;
  mods: Partial<SimMods>;
  skipInsertion: boolean;
}

const KEY = 'nightdrop.replay.latest';
/** localStorage is small and shared with the saves: a long mouse-aimed mission may not fit, and that is fine. */
const MAX_CHARS = 1_500_000;

export function missionFor(source: MissionSource): MissionDef {
  return source.kind === 'depot' ? buildMunitionsDepot(source.seed) : generateMission(source.params);
}

/** True when it was kept. */
export function saveLatestReplay(file: ReplayFile): boolean {
  const text = JSON.stringify(file);
  if (text.length > MAX_CHARS) return false;
  browserStorage.save(KEY, text);
  return browserStorage.load(KEY) === text;
}

export function loadLatestReplay(): ReplayFile | null {
  const raw = browserStorage.load(KEY);
  if (!raw) return null;
  try {
    const file = JSON.parse(raw) as Partial<ReplayFile>;
    if (file.version !== 1 || !file.source || !Array.isArray(file.runs) || typeof file.finalHash !== 'string') return null;
    return file as ReplayFile;
  } catch {
    return null;
  }
}
